import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Tag, Modal, message, Card, theme, Form, Input, Select, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import { ADMIN_API } from '../constants/api';
import axiosInstance from '../services/axiosConfig';
import moment from 'moment';
import permissionService from '../services/permissionService';

const { Option } = Select;

function AdminList() {
  const [data, setData] = useState({ items: [], total: 0, currentPage: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [form] = Form.useForm();
  const { token } = theme.useToken();

  // Lấy danh sách quyền từ permissionService
  const permissions = permissionService.getAllPermissions();

  // Lấy danh sách admin
  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ADMIN_API.LIST);
      setData({
        items: response.data.users || [],
        total: response.data.total || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      });
    } catch (error) {
      message.error('Không thể tải danh sách tài khoản');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'UserID',
      key: 'UserID',
      width: 100,
    },
    {
      title: 'Tên đăng nhập',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: 'Họ và tên',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (role) => (
        <Tag color="blue">
          {role === 'admin' ? 'Quản trị viên' : role}
        </Tag>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Hoạt động' : 'Khóa'}
        </Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingAdmin(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = async (record) => {
    try {
      // Lấy quyền của user
      const userPermissions = await permissionService.getUserPermissions(record.UserID);
      setEditingAdmin(record);
      form.setFieldsValue({
        ...record,
        password: '', // Không hiển thị mật khẩu khi edit
        permissions: userPermissions
      });
      setIsModalVisible(true);
    } catch (error) {
      message.error('Không thể lấy thông tin quyền của tài khoản');
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa tài khoản ${record.username}?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await axiosInstance.delete(ADMIN_API.DELETE(record.UserID));
          message.success('Xóa tài khoản thành công!');
          fetchAdmins();
        } catch (error) {
          message.error('Có lỗi xảy ra khi xóa tài khoản');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        isActive: values.isActive ?? true,
      };

      if (editingAdmin) {
        // Cập nhật thông tin cơ bản
        await axiosInstance.put(ADMIN_API.UPDATE(editingAdmin.UserID), submitData);
        // Cập nhật quyền
        await permissionService.updateUserPermissions(editingAdmin.UserID, values.permissions);
        message.success('Cập nhật tài khoản thành công!');
      } else {
        const response = await axiosInstance.post(ADMIN_API.CREATE, submitData);
        // Thêm quyền cho tài khoản mới
        await permissionService.updateUserPermissions(response.data.user.UserID, values.permissions);
        message.success('Thêm tài khoản thành công!');
      }
      setIsModalVisible(false);
      fetchAdmins();
    } catch (error) {
      message.error('Có lỗi xảy ra: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div style={{ padding: '16px', backgroundColor: token.colorBgContainer }}>
      <Card
        title={<h2 style={{ margin: 0 }}>Quản lý tài khoản</h2>}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Thêm tài khoản mới
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={data.items}
          rowKey="id"
          loading={loading}
          pagination={{
            current: data.currentPage,
            pageSize: 10,
            total: data.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Tổng số ${total} bản ghi`,
          }}
        />
      </Card>   

      <Modal
        title={editingAdmin ? 'Sửa tài khoản' : 'Thêm tài khoản mới'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ isActive: true }}
        >
          <Form.Item
            name="username"
            label="Tên đăng nhập"
            rules={[{ required: true, message: 'Vui lòng nhập tên đăng nhập' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Nhập tên đăng nhập" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Mật khẩu"
            rules={[
              { required: !editingAdmin, message: 'Vui lòng nhập mật khẩu' },
              { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Nhập mật khẩu" />
          </Form.Item>

          <Form.Item
            name="fullName"
            label="Họ và tên"
            rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
          >
            <Input placeholder="Nhập họ và tên" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Vui lòng nhập email' },
              { type: 'email', message: 'Email không hợp lệ' }
            ]}
          >
            <Input placeholder="Nhập email" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Số điện thoại"
            rules={[{ required: true, message: 'Vui lòng nhập số điện thoại' }]}
          >
            <Input placeholder="Nhập số điện thoại" />
          </Form.Item>

          <Form.Item
            name="permissions"
            label="Quyền hạn"
            rules={[{ required: true, message: 'Vui lòng chọn quyền hạn' }]}
          >
            <Select
              mode="multiple"
              placeholder="Chọn quyền hạn"
              style={{ width: '100%' }}
            >
              {permissions.map(permission => (
                <Option key={permission.key} value={permission.key}>
                  {permission.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Trạng thái"
            valuePropName="checked"
          >
            <Switch checkedChildren="Hoạt động" unCheckedChildren="Khóa" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsModalVisible(false)}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit">
                {editingAdmin ? 'Cập nhật' : 'Thêm mới'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminList; 